import { useCallback, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { CustomerPackageAdjustModal } from '@/components/masters/CustomerPackageAdjustModal'
import {
  adjustCustomerPackage,
  fetchCustomerPackageProfile,
  fetchCustomerPackageRedemptions,
  formatCustomerPackageAmount,
  formatCustomerPackageDate,
  formatCustomerPackageStatus,
  redemptionItemLabel,
} from '@/lib/api/customerPackages'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useAuth } from '@/contexts/AuthContext'
import { hasPermission, PERMISSIONS } from '@/lib/permissions'
import { formatPackageExpiryLabel } from '@/lib/pos/posCustomerPackageBalances'
import { posPackageTypeLabel } from '@/lib/pos/posPackage'
import { cn } from '@/lib/utils'

/**
 * @param {{ customerId: number | null }} props
 */
export function CustomerPackagesTab({ customerId }) {
  const { user } = useAuth()
  const canAdjust = hasPermission(user, PERMISSIONS.PACKAGES_ADJUST)
  const [packages, setPackages] = useState([])
  const [redemptions, setRedemptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [adjustTarget, setAdjustTarget] = useState(null)
  const [adjustSubmitting, setAdjustSubmitting] = useState(false)
  const [adjustError, setAdjustError] = useState('')

  const loadProfile = useCallback(async () => {
    if (!customerId) {
      setPackages([])
      setRedemptions([])
      setError('')
      return
    }

    setLoading(true)
    setError('')

    try {
      const [packageRows, redemptionRows] = await Promise.all([
        fetchCustomerPackageProfile(customerId),
        fetchCustomerPackageRedemptions(customerId),
      ])
      setPackages(packageRows)
      setRedemptions(redemptionRows)
    } catch (err) {
      setPackages([])
      setRedemptions([])
      setError(apiErrorMessage(err, 'Unable to load package profile.'))
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => {
    loadProfile()
  }, [loadProfile])

  async function handleAdjustSubmit(payload) {
    if (!adjustTarget) return

    setAdjustSubmitting(true)
    setAdjustError('')

    try {
      const updated = await adjustCustomerPackage(adjustTarget.customer_package_id, payload)
      setPackages((current) => current.map((row) => (
        row.customer_package_id === updated.customer_package_id ? updated : row
      )))
      setAdjustTarget(null)
    } catch (err) {
      setAdjustError(apiErrorMessage(err, 'Unable to save package adjustment.'))
    } finally {
      setAdjustSubmitting(false)
    }
  }

  if (!customerId) {
    return (
      <p className="text-sm text-muted-foreground">
        Save the customer profile first to view purchased packages.
      </p>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading packages…
      </div>
    )
  }

  if (error) {
    return (
      <p className="py-4 text-sm text-destructive" role="alert">
        {error}
      </p>
    )
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold">Purchased packages</h2>
          <p className="text-xs text-muted-foreground">
            Active, expired, and fully redeemed packages for this guest.
          </p>
        </div>

        {packages.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground">
            <p>No packages on this guest yet.</p>
            <p className="mt-2">
              Packages are added when you <strong>sell</strong> one at POS — not from this screen.
            </p>
            <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-xs">
              <li>Create the package under <strong>Masters → Packages</strong> (Active + Sellable on POS).</li>
              <li>Open <strong>POS</strong>, assign this guest (<strong>F2</strong>).</li>
              <li>Press <strong>F6</strong> (Package), pick the package, add to ticket.</li>
              <li>Complete payment (<strong>F4</strong>) — the balance then appears here.</li>
            </ol>
            <p className="mt-3 text-xs">
              Managers can adjust an existing balance with <strong>Adjust</strong> after a package is purchased.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {packages.map((pkg) => {
              const expiry = formatPackageExpiryLabel(pkg.expires_at)

              return (
                <li
                  key={pkg.customer_package_id}
                  className="rounded-2xl border border-border/70 bg-muted/15 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium">{pkg.package_name}</p>
                      <p className="mt-0.5 text-xs uppercase tracking-wide text-primary">
                        {posPackageTypeLabel(pkg.package_type)}
                      </p>
                    </div>
                    <span className={cn(
                      'rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase',
                      pkg.status === 'active'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-muted text-muted-foreground',
                    )}
                    >
                      {formatCustomerPackageStatus(pkg.status)}
                    </span>
                    {canAdjust && pkg.status !== 'cancelled' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setAdjustError('')
                          setAdjustTarget(pkg)
                        }}
                        className="rounded-lg border border-border/70 px-2.5 py-1 text-xs font-medium hover:bg-muted/80"
                      >
                        Adjust
                      </button>
                    ) : null}
                  </div>

                  <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                    <p>
                      <span className="text-muted-foreground">Purchased:</span>{' '}
                      {formatCustomerPackageDate(pkg.purchased_at)}
                    </p>
                    <p>
                      <span className="text-muted-foreground">Paid:</span>{' '}
                      {formatCustomerPackageAmount(pkg.purchase_price)}
                    </p>
                    {expiry ? (
                      <p>
                        <span className="text-muted-foreground">Validity:</span> {expiry}
                      </p>
                    ) : null}
                    {pkg.voucher_code ? (
                      <p>
                        <span className="text-muted-foreground">Voucher code:</span>{' '}
                        <span className="font-mono">{pkg.voucher_code}</span>
                      </p>
                    ) : null}
                  </div>

                  <p className="mt-3 text-sm font-medium">{pkg.headline}</p>

                  {pkg.details.length > 0 ? (
                    <ul className="mt-2 space-y-1 border-t border-border/50 pt-2">
                      {pkg.details.map((row) => (
                        <li
                          key={`${pkg.customer_package_id}-${row.label}`}
                          className="flex items-center justify-between gap-3 text-xs"
                        >
                          <span className="text-muted-foreground">{row.label}</span>
                          <span className="font-medium">{row.value}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <p className="mt-2 text-xs text-muted-foreground">
                    {pkg.redemption_count} redemption(s)
                  </p>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold">Redemption history</h2>
          <p className="text-xs text-muted-foreground">
            Services and products redeemed against this guest&apos;s packages.
          </p>
        </div>

        {redemptions.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground">
            No redemptions recorded yet.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Package</th>
                  <th className="px-4 py-3 font-medium">Item</th>
                  <th className="px-4 py-3 font-medium">Qty</th>
                  <th className="px-4 py-3 font-medium">By</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((row) => (
                  <tr key={row.id} className="border-t border-border/50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {formatCustomerPackageDate(row.redeemed_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div>{row.package_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {posPackageTypeLabel(row.package_type)}
                      </div>
                    </td>
                    <td className="px-4 py-3">{redemptionItemLabel(row)}</td>
                    <td className="px-4 py-3">{row.quantity}</td>
                    <td className="px-4 py-3">{row.redeemed_by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <CustomerPackageAdjustModal
        open={Boolean(adjustTarget)}
        pkg={adjustTarget}
        submitting={adjustSubmitting}
        error={adjustError}
        onClose={() => {
          if (!adjustSubmitting) {
            setAdjustTarget(null)
            setAdjustError('')
          }
        }}
        onSubmit={handleAdjustSubmit}
      />
    </div>
  )
}
